import {createContext, useContext, useState} from 'react';
import {createDrawerNavigator} from '@react-navigation/drawer';
import {createStackNavigator} from '@react-navigation/stack';
import {useAppSettings} from '../components/AppSettings';

import Settings from './Settings';
import {CustomAppBar} from './AppBar';
import {ProfileSettingsModal} from './ProfileSettingsModal';
import {ProfileSettingsScreen} from './ProfileSettingsScreen';
import {NotFound} from '../components/NotFound';
import {FirestoreUserGate} from '../components/FirestoreGate';

import Funds from './Funds';
import CreditTypes from './CreditTypes';
import ExchangeRates from './ExchangeRates';
import LimitedPartners from './LimitedPartners';
import Members from './Members';
import People from './People';
import Properties from './Properties';
import Reservations from './Reservations';
import Subscribers from './Subscribers';
import Transactions from './Transactions';
import {
  GuestyMatchedReservations,
  GuestyUnmatchedReservations,
} from './GuestyReservationMatch';
import {LayoutSize, useLayoutInfo} from '../hooks/useLayoutInfo';
import {Person, PersonInfo} from '../hooks/usePersonInfo';
import {Search} from './Search';
import {Reports} from './Reports';
import {InvestorDetails} from './InvestorDetails';
import GuestyGuestMatch from './GuestyGuestMatch';
import GuestySettings from './GuestySettings';
import GuestyPropertyMatch from './GuestyPropertyMatch';
import GuestySynchronization from './GuestySynchronization';
import AdminTools from './AdminTools';
import ReservationYears from './ReservationYears';
import AnnualCreditsAllocation from './AnnualCreditAllocations';
import {useReservationYears} from '../hooks/useDomainData';
// import Credits from './Credits';

// This context contains the idea of who we are, and who we are working on
export type FilteredPersonInfo = {
  authPerson?: PersonInfo;
  workingOnPerson?: Person;
};

// eslint-disable-next-line no-spaced-func
export const PersonInfoContext = createContext<
  | {
      filteredPersonInfo?: FilteredPersonInfo;
      setFilteredPersonInfo: (filteredPersonInfo: FilteredPersonInfo) => void;
    }
  | undefined
>(undefined);

const RootStackNav = createStackNavigator();
export const RootStack = () => {
  const appSettings = useAppSettings();

  return (
    <RootStackNav.Navigator
      initialRouteName="App"
      screenOptions={{headerShown: false}}>
      <RootStackNav.Screen name="App" component={MainDrawer} />
      <RootStackNav.Screen
        name="ProfileSettingsModal"
        component={ProfileSettingsModal}
        options={{
          presentation: 'transparentModal',
          // cardOverlayEnabled: true,
        }}
      />
      <RootStackNav.Screen
        name="NotFound"
        component={NotFound}
        options={{title: appSettings.t('NotFound')}}
      />
    </RootStackNav.Navigator>
  );
};

// Custom App Bar
// -- has switch for tab nav
// -- has branding
// -- has profile on right side / switch for end tab nav

const ProfileSettingsStackNav = createStackNavigator();
export const ProfileSettingsStack = () => {
  const appSettings = useAppSettings();
  return (
    <ProfileSettingsStackNav.Navigator
      // screenOptions={{cardStyle: {width: '100%', height: '100%', flex: 1}}}
      initialRouteName="ProfileSettingsScreen">
      <ProfileSettingsStackNav.Screen
        name="ProfileSettingsScreen"
        options={{title: appSettings.t('ProfileUserAppSettingsTitle')}}
        component={ProfileSettingsScreen}
      />
      <ProfileSettingsStackNav.Screen
        options={{title: appSettings.t('ProfileUserSettingsTitle')}}
        name="Settings"
        component={Settings}
      />
    </ProfileSettingsStackNav.Navigator>
  );
};

//
// -- end tab nav (fully custom contents):
//   -- ProfileSettingsStack
//     -- ProfileSettingsScreen
//       -- X at top right to close
//       -- Account Info Chunk
//       -- Sign Out button
//     -- AccountSwitchScreen
//     -- AccountSettingsScreen

//
// -- MainDrawer
const MainDrawerNav = createDrawerNavigator();
export const MainDrawer = () => {
  const layoutInfo = useLayoutInfo();
  const personInfo = useContext(PersonInfoContext);
  const reservationYears = useReservationYears();

  return (
    <MainDrawerNav.Navigator
      initialRouteName={
        personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
        personInfo?.filteredPersonInfo?.authPerson?.isGuestRelations
          ? 'search'
          : 'investor'
      }
      screenOptions={{
        // minimum width to hold our labels
        drawerStyle: {width: 240},

        // tighten up the vertical spacing
        drawerItemStyle: {padding: 0},
        drawerLabelStyle: {marginVertical: -5},

        headerShown: false,
        drawerType:
          layoutInfo.hasBeenSmall || layoutInfo.layoutSize !== LayoutSize.large
            ? 'front'
            : 'permanent',
      }}>
      {(personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
        personInfo?.filteredPersonInfo?.authPerson?.isGuestRelations) && (
        <MainDrawerNav.Group>
          <MainDrawerNav.Screen
            name="search"
            options={{title: 'Search'}}
            component={Search}
          />
        </MainDrawerNav.Group>
      )}
      {(personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
        personInfo?.filteredPersonInfo?.authPerson?.isGuestRelations) && (
        <MainDrawerNav.Group>
          <MainDrawerNav.Screen
            name="reports"
            options={{title: 'Reports'}}
            component={Reports}
          />
        </MainDrawerNav.Group>
      )}
      {/* <MainDrawerNav.Screen name='Credits' component={Credits} /> */}
      <MainDrawerNav.Screen
        // @ts-ignore
        name="investor"
        options={{title: 'Investor Details'}}
        // @ts-ignore
        component={InvestorDetails}
      />
      <MainDrawerNav.Screen
        name="Reservations"
        options={{title: 'Reservations'}}
        component={Reservations}
      />
      <MainDrawerNav.Screen
        name="Transactions"
        options={{title: 'Transactions'}}
        component={Transactions}
      />
      <MainDrawerNav.Screen
        name="Properties"
        options={{title: 'Properties'}}
        component={Properties}
      />
      {(personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
        personInfo?.filteredPersonInfo?.authPerson?.isGuestRelations) && (
        <MainDrawerNav.Group>
          <MainDrawerNav.Screen
            name="Subscribers"
            options={{title: 'Subscribers'}}
            component={Subscribers}
          />
          <MainDrawerNav.Screen
            name="Limited Partners"
            options={{title: 'Limited Partners'}}
            component={LimitedPartners}
          />
          <MainDrawerNav.Screen
            name="Members"
            options={{title: 'Members'}}
            component={Members}
          />
          <MainDrawerNav.Screen
            name="People"
            options={{title: 'People'}}
            component={People}
          />
          <MainDrawerNav.Screen name="Portal Management" component={Funds} />
          <MainDrawerNav.Group
            screenOptions={{
              drawerLabelStyle: {
                paddingLeft: 15,
                marginVertical: -5,
                marginTop: -15,
              },
            }}>
            <MainDrawerNav.Screen
              name="Admin Tools"
              options={{title: `${String.fromCharCode(8226)} Admin Tools`}}
              component={AdminTools}
            />
            <MainDrawerNav.Screen
              name="Funds"
              options={{
                title: `${String.fromCharCode(8226)} Funds`,
              }}
              component={Funds}
            />
            <MainDrawerNav.Screen
              name="Credit Types"
              options={{title: `${String.fromCharCode(8226)} Credit Types`}}
              component={CreditTypes}
            />
            <MainDrawerNav.Screen
              name="Exchange Rates"
              options={{title: `${String.fromCharCode(8226)} Exchange Rates`}}
              component={ExchangeRates}
            />
            <MainDrawerNav.Screen
              name="Reservation Years"
              key="reservation-year-general"
              options={{
                title: `${String.fromCharCode(8226)} Reservation Years`,
              }}
              component={ReservationYears}
            />
            <>
              {reservationYears &&
                reservationYears.length > 0 &&
                reservationYears.map(resYear => (
                  <MainDrawerNav.Screen
                    key={`annual-credits-${resYear.name}`}
                    name={`Annual Credits ${resYear.name}`}
                    options={{
                      title: `${String.fromCharCode(8226)} Annual ${
                        resYear.name
                      }`,
                    }}>
                    {() => <AnnualCreditsAllocation resYear={resYear.name} />}
                  </MainDrawerNav.Screen>
                ))}
            </>
          </MainDrawerNav.Group>
          <MainDrawerNav.Screen
            name="Guesty Integration"
            component={GuestyUnmatchedReservations}
          />
          <MainDrawerNav.Group
            screenOptions={{
              drawerLabelStyle: {
                paddingLeft: 15,
                marginVertical: -5,
                marginTop: -15,
              },
            }}>
            <MainDrawerNav.Screen
              name="Reservation Match"
              options={{
                title: `${String.fromCharCode(8226)} Reservation Match`,
              }}
              component={GuestyUnmatchedReservations}
            />
            <MainDrawerNav.Screen
              name="Matched Reservations"
              options={{
                title: `${String.fromCharCode(8226)} Matched Reservations`,
              }}
              component={GuestyMatchedReservations}
            />
            <MainDrawerNav.Screen
              name="Guest Match"
              options={{
                title: `${String.fromCharCode(8226)} Guest Match`,
              }}
              component={GuestyGuestMatch}
            />
            <MainDrawerNav.Screen
              name="Property Match"
              options={{
                title: `${String.fromCharCode(8226)} Property Match`,
              }}
              component={GuestyPropertyMatch}
            />
            <MainDrawerNav.Screen
              name="Synchronization"
              options={{
                title: `${String.fromCharCode(8226)} Synchronization`,
              }}
              component={GuestySynchronization}
            />
            <MainDrawerNav.Screen
              name="Settings"
              options={{
                title: `${String.fromCharCode(8226)} Settings`,
              }}
              component={GuestySettings}
            />
          </MainDrawerNav.Group>
        </MainDrawerNav.Group>
      )}
    </MainDrawerNav.Navigator>
  );
};

const SignedIn = () => {
  const [filteredPersonInfo, setFilteredPersonInfo] =
    useState<FilteredPersonInfo>();
  const updateFilteredPersonInfo = (newInfo: FilteredPersonInfo) => {
    setFilteredPersonInfo(newInfo);
  };

  return (
    <PersonInfoContext.Provider
      value={{
        filteredPersonInfo,
        setFilteredPersonInfo: updateFilteredPersonInfo,
      }}>
      <FirestoreUserGate>
        <CustomAppBar>
          <RootStack />
        </CustomAppBar>
      </FirestoreUserGate>
    </PersonInfoContext.Provider>
  );
};

export default SignedIn;
